/**
 * @file TherapistLanding.jsx
 * @description The providers' marketing page (/aba-case-finder): hero, the real product screens, six reasons to
 *   join, how it works, the free Pay & Hours tracker, a short comparison, FAQ, then a quiet ending (one call to
 *   action per page: the hero). Each idea is said once.
 * @author Victor Carpinelli
 * @version 2.0.0
 * @module components/TherapistLanding
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  MapPinIcon, UsersIcon, CheckIcon, ShieldIcon, StarIcon, DollarIcon,
  BriefcaseIcon, LockIcon, DownloadIcon, AwardIcon, GlobeIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import QuietEnding from "./QuietEnding";
import HeroAlertsCta from "./HeroAlertsCta";
import TrackerShowcase from "./TrackerShowcase";
import DiscoveryShowcase from "./DiscoveryShowcase";
import { usePageSeo } from "../lib/seo";
import { useSiteSchema } from "../lib/seoSchema";

const D = {
  navy:       "#0F1024",
  navyMid:    "#1E2140",
  accent:     T.blue,
  accentGlow: "rgba(79,70,229,0.18)",
  green:      T.green,
  border:     "rgba(226,232,240,0.8)",
  textMid:    "#475569",
  textLight:  "#94A3B8",
  radius:     14,
};

// ── Feature cards ─────────────────────────────────────────────────────────────
const FEATURES = [
  { Icon: StarIcon,       titleKey: "therapistLanding.feature1Title", descKey: "therapistLanding.feature1Desc" },
  { Icon: MapPinIcon,     titleKey: "therapistLanding.feature2Title", descKey: "therapistLanding.feature2Desc" },
  { Icon: DollarIcon,     titleKey: "therapistLanding.feature3Title", descKey: "therapistLanding.feature3Desc" },
  { Icon: LockIcon,       titleKey: "therapistLanding.feature4Title", descKey: "therapistLanding.feature4Desc" },
  { Icon: AwardIcon,      titleKey: "therapistLanding.feature5Title", descKey: "therapistLanding.feature5Desc" },
  { Icon: BriefcaseIcon,  titleKey: "therapistLanding.feature6Title", descKey: "therapistLanding.feature6Desc" },
];

// ── Hero chips: the trust line in three words each ("free" once) ───────────
const HERO_CHIPS = [
  { Icon: ShieldIcon, key: "therapistLanding.badgeNpi" },
  { Icon: CheckIcon,  key: "therapistLanding.badgeFree" },
  { Icon: LockIcon,   key: "therapistLanding.badgePrivate" },
];

// ── How it works steps ────────────────────────────────────────────────────────
const HOW_IT_WORKS = [
  { n: "01", titleKey: "therapistLanding.how1Title", descKey: "therapistLanding.how1Desc", Icon: UsersIcon },
  { n: "02", titleKey: "therapistLanding.how2Title", descKey: "therapistLanding.how2Desc", Icon: MapPinIcon },
  { n: "03", titleKey: "therapistLanding.how3Title", descKey: "therapistLanding.how3Desc", Icon: CheckIcon },
];

// ── Comparison rows ───────────────────────────────────────────────────────────
const VS_ROWS = [
  { labelKey: "therapistLanding.vs1Label",       aba: "yes", generic: "no"        },
  { labelKey: "therapistLanding.vs2Label",       aba: "yes", generic: "no"        },
  { labelKey: "therapistLanding.vs4Label",       aba: "yes", generic: "vague"     },
  { labelKey: "therapistLanding.vsNpiLabel",     aba: "yes", generic: "no"        },
  { labelKey: "therapistLanding.vsPrivateLabel", aba: "yes", generic: "no"        },
  { labelKey: "therapistLanding.vsFreshLabel",   aba: "yes", generic: "no"        },
  { labelKey: "therapistLanding.vsRemoteLabel",  aba: "yes", generic: "no"        },
  { labelKey: "therapistLanding.vs5Label",       aba: "yes", generic: "blackhole" },
  { labelKey: "therapistLanding.vs9Label",       aba: "yes", generic: "no"        },
  { labelKey: "therapistLanding.vs8Label",       aba: "yes", generic: "usually"   },
];

// ── FAQ items ─────────────────────────────────────────────────────────────────
// Privacy first and open (provider persona review, 2026-10-19: "that's THE question").
const FAQ_ITEMS = [
  { qKey: "therapistLanding.faq5Q",  aKey: "therapistLanding.faq5A" },
  { qKey: "therapistLanding.faq3Q",  aKey: "therapistLanding.faq3A" },
  { qKey: "therapistLanding.faq10Q", aKey: "therapistLanding.faq10A" },
  { qKey: "therapistLanding.faq9Q",  aKey: "therapistLanding.faq9A" },
  { qKey: "therapistLanding.faq1Q",  aKey: "therapistLanding.faq1A" },
  { qKey: "therapistLanding.faq2Q",  aKey: "therapistLanding.faq2A" },
  { qKey: "therapistLanding.faq4Q",  aKey: "therapistLanding.faq4A" },
  { qKey: "therapistLanding.faq6Q",  aKey: "therapistLanding.faq6A" },
  { qKey: "therapistLanding.faq7Q",  aKey: "therapistLanding.faq7A" },
  { qKey: "therapistLanding.faq8Q",  aKey: "therapistLanding.faq8A" },
  // Questions BCBAs ask before signing up (owner 2026-10-23): who credentials you with insurers, and whether families use the site.
  { qKey: "therapistLanding.faq11Q", aKey: "therapistLanding.faq11A" },
  { qKey: "therapistLanding.faq12Q", aKey: "therapistLanding.faq12A" },
];

// ── Tracker showcase trust pills (privacy + why-free objection handling) ────────
const TRACKER_TRUST = [
  { Icon: LockIcon,     titleKey: "therapistLanding.trackerTrust1" },
  { Icon: ShieldIcon,   titleKey: "therapistLanding.trackerTrust2" },
  { Icon: DownloadIcon, titleKey: "therapistLanding.trackerTrust3" },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function FeatureCard({ Icon, titleKey, descKey, title, desc }) {
  const { t } = useTranslation();
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedDesc  = descKey  ? t(descKey)  : desc;
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "#F8FAFF" : "#fff",
        border: `1.5px solid ${hovered ? D.accent + "44" : D.border}`,
        borderRadius: D.radius,
        padding: "28px 24px",
        display: "flex", flexDirection: "column", gap: 14,
        transition: "all 0.18s ease",
        boxShadow: hovered ? `0 8px 32px ${D.accentGlow}` : "0 1px 4px rgba(15,23,42,0.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12, flexShrink: 0,
          background: hovered ? D.accent : "#F1F5F9",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.18s ease",
        }}>
          <Icon size={19} color={hovered ? "#fff" : D.accent} />
        </div>
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: D.navy, marginBottom: 6 }}>{resolvedTitle}</div>
        <div style={{ fontSize: 13, color: D.textMid, lineHeight: 1.65 }}>{resolvedDesc}</div>
      </div>
    </div>
  );
}

/**
 * @description Renders a single check cell in the comparison table.
 * @param {"yes"|"no"|"vague"|"blackhole"|"usually"} type
 */
function VsCheck({ type }) {
  const { t } = useTranslation();
  if (type === "yes") return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 26, height: 26, borderRadius: "50%",
      background: "#ecfdf5", border: "1.5px solid #10b981", color: "#10b981",
      fontSize: 13, fontWeight: 700,
    }}>✓</span>
  );
  if (type === "no") return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 26, height: 26, borderRadius: "50%",
      background: "#fef2f2", border: "1.5px solid #fca5a5", color: "#ef4444",
      fontSize: 13, fontWeight: 700,
    }}>✗</span>
  );
  const label = type === "vague" ? t('therapistLanding.vsVague') : type === "blackhole" ? t('therapistLanding.vsBlackhole') : t('therapistLanding.vsUsually');
  return (
    <span style={{
      display: "inline-block", fontSize: 11, fontWeight: 700,
      color: "#92400e", background: "#fffbeb",
      border: "1px solid #fcd34d", borderRadius: 20, padding: "2px 8px",
    }}>{label}</span>
  );
}

/**
 * @description Collapsible FAQ item with toggle.
 * @param {{ q: string, a: string, defaultOpen?: boolean }} props
 */
function FaqItem({ q, a, qKey, aKey, defaultOpen = false }) {
  const { t } = useTranslation();
  const question = qKey ? t(qKey) : q;
  const answer   = aKey ? t(aKey) : a;
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      background: "#fff",
      border: `1.5px solid ${open ? D.accent + "33" : D.border}`,
      borderRadius: 12, marginBottom: 10, overflow: "hidden",
      transition: "border-color 0.18s",
    }}>
      <button
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", textAlign: "left", padding: "18px 22px",
          background: "none", border: "none", cursor: "pointer",
          fontSize: 14, fontWeight: 700, color: D.navy,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          fontFamily: "'Inter','DM Sans',sans-serif",
        }}
      >
        {question}
        <span style={{
          fontSize: 18, color: D.accent, flexShrink: 0,
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
          display: "inline-block",
        }}>⌄</span>
      </button>
      {open && (
        <div style={{ padding: "0 22px 18px", fontSize: 13, color: D.textMid, lineHeight: 1.75 }}>
          {answer}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @description Marketing landing page for therapist (RBT/BCBA/BCaBA) visitors.
 * @param {{ onLearnMoreTracker: Function, onNavigate?: Function, embedded?: boolean }} props  Joining happens from the top bar's Sign up button on desktop; on phones (where that button is small) the hero also carries it.
 */
export default function TherapistLanding({ onLearnMoreTracker, onNavigate, embedded = false }) {
  const { t, i18n } = useTranslation();
  // "En español" chip for English readers only (the Spanish page already is).
  const isSpanish = String(i18n.language || "").toLowerCase().startsWith("es");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Standalone (/aba-case-finder) owns the head incl. canonical + OG; embedded as
  // a HomePage section it's a no-op (the host page owns the head).
  usePageSeo(embedded ? {} : {
    title: "Find ABA Cases Near You: RBT & BCBA Jobs | ABAcases",
    description: "Browse ABA cases by location, pay range, and schedule, and filter to the ones that fit your skills and credentials, not just who applied last. Free to join.",
    canonical: "/aba-case-finder",
  });
  useSiteSchema(!embedded); // global Organization + WebSite (standalone only)

  const sectionPad = isMobile ? "56px 16px" : "80px 48px";

  return (
    <div style={embedded
      ? { fontFamily: "'Inter','DM Sans',sans-serif" }
      : { flex: 1, overflowY: "auto", background: "#F8FAFC", fontFamily: "'Inter','DM Sans',sans-serif" }}>

      {/* ── Hero (suppressed when embedded as a HomePage section) ──── */}
      {!embedded && (
      <div style={{
        background: `linear-gradient(135deg, ${D.navy} 0%, ${D.navyMid} 60%, #1a1f4a 100%)`,
        padding: isMobile ? "56px 16px 36px" : "80px 48px 56px",
        textAlign: "center", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -80, left: "20%", width: 400, height: 400, borderRadius: "50%", background: "rgba(79,70,229,0.18)", filter: "blur(80px)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -60, right: "15%", width: 300, height: 300, borderRadius: "50%", background: "rgba(139,92,246,0.15)", filter: "blur(60px)", pointerEvents: "none" }} />

        <div style={{ position: "relative", maxWidth: 760, margin: "0 auto" }}>
          <div data-testid="hero-eyebrow" style={{ display: "inline-block", marginBottom: 16, padding: "5px 12px", borderRadius: 100, background: "rgba(165,180,252,0.12)", border: "1px solid rgba(165,180,252,0.3)", color: "#c7d2fe", fontSize: 13, fontWeight: 700, letterSpacing: "0.01em" }}>
            {t("therapistLanding.heroEyebrow")}
          </div>
          <h1 style={{
            fontSize: "clamp(34px, 5vw, 58px)", fontWeight: 900, color: "#fff",
            lineHeight: 1.12, letterSpacing: "-0.03em", margin: "0 0 20px",
          }}>
            {t('therapistLanding.heroTitle')}
          </h1>

          <p style={{ fontSize: 17, color: "rgba(255,255,255,0.62)", lineHeight: 1.7, maxWidth: 620, margin: "0 auto" }}>
            {t('therapistLanding.heroSubtitle')}
          </p>

          {/* Phones only (owner rule 2026-10-19 keeps desktop heroes button-free; the top bar's 29px button is too small to tap on a phone). */}
          {isMobile && onNavigate && (
            <div style={{ marginTop: 22 }}>
              <HeroAlertsCta label={t("providerPages.getCaseAlerts")} pagePath="/aba-case-finder/" pageType="provider" audience="provider" onNavigate={onNavigate} isMobile />
            </div>
          )}


          <div data-testid="hero-chips" style={{ marginTop: 26, display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
            {[...HERO_CHIPS, ...(isSpanish ? [] : [{ Icon: GlobeIcon, key: "therapistLanding.badgeSpanish" }])].map(({ Icon, key }) => (
              <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 12px", borderRadius: 100, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", color: "rgba(255,255,255,0.82)", fontSize: 12.5, fontWeight: 600 }}>
                <Icon size={13} color="#a5b4fc" />{t(key)}
              </span>
            ))}
          </div>

        </div>
      </div>
      )}

      {/* ── Discovery showcase — see the therapist experience in motion ── */}
      <div id="see-it-in-action" style={{ scrollMarginTop: 16, background: `linear-gradient(180deg, #1a1f4a 0%, ${D.navy} 100%)`, padding: isMobile ? "28px 12px 56px" : "40px 48px 80px" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 22 : 28, maxWidth: 680, marginLeft: "auto", marginRight: "auto" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a5b4fc", fontFamily: "'JetBrains Mono', monospace" }}>
            {t("therapistLanding.discoveryLabel", "See it in action")}
          </span>
          <h2 style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", margin: "8px 0 0" }}>
            {t("therapistLanding.discoveryTitle", "From map, to match, to message")}
          </h2>
        </div>
        <DiscoveryShowcase isMobile={isMobile} accent={D.accent} />
      </div>

      {/* ── Features ──────────────────────────────────────────────── */}
      <div style={{ padding: sectionPad, maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 52 }}>
          <h2 style={{ fontSize: 34, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: "0 0 10px" }}>
            {t('therapistLanding.featuresTitle')}
          </h2>
          <p style={{ fontSize: 15, color: D.textMid, maxWidth: 560, margin: "0 auto" }}>
            {t('therapistLanding.featuresSubtitle')}
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 20 }}>
          {FEATURES.map(f => <FeatureCard key={f.titleKey || f.title} {...f} />)}
        </div>
      </div>

      {/* ── How it works ──────────────────────────────────────────── */}
      <div style={{ background: "#F4F4FD", padding: sectionPad, borderTop: `1px solid ${D.border}` }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <h2 style={{ fontSize: isMobile ? 28 : 32, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: 0 }}>
              {t('therapistLanding.howItWorksTitle')}
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 32 }}>
            {HOW_IT_WORKS.map(step => (
              <div key={step.n} style={{ textAlign: "center" }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: "#fff", border: `1.5px solid ${D.accent}33`, boxShadow: `0 6px 18px ${D.accentGlow}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 18px",
                }}>
                  <step.Icon size={22} color={D.accent} />
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: D.accent, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", marginBottom: 8 }}>
                  {t('therapistLanding.stepLabel')} {step.n}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: D.navy, marginBottom: 8 }}>{t(step.titleKey)}</div>
                <div style={{ fontSize: 13.5, color: D.textMid, lineHeight: 1.65 }}>{t(step.descKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Pay & Hours tracker showcase (free bonus tool) ────────── */}
      <div id="pay-tracker" style={{
        scrollMarginTop: 16,
        background: `linear-gradient(180deg, ${D.navy} 0%, #1a1f4a 100%)`,
        padding: isMobile ? "52px 12px 60px" : "72px 48px 88px",
      }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 28 : 40, maxWidth: 680, marginLeft: "auto", marginRight: "auto" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a5b4fc", fontFamily: "'JetBrains Mono', monospace" }}>
            {t("therapistLanding.trackerEyebrow", "Free bonus · built in")}
          </span>
          <h2 style={{ fontSize: isMobile ? 25 : 32, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", margin: "10px 0 12px" }}>
            {t("therapistLanding.trackerTitle", "Free Pay & Hours tracker for ABA therapists")}
          </h2>
          <p style={{ fontSize: 14.5, color: "rgba(255,255,255,0.66)", lineHeight: 1.7, maxWidth: 600, margin: "0 auto" }}>
            {t("therapistLanding.trackerSubtitle", "Log billable hours across every agency, know exactly what you're owed, catch late payments, and walk into tax season ready. Free for providers.")}
          </p>
        </div>

        <TrackerShowcase isMobile={isMobile} accent={D.accent} />

        {/* Trust row — privacy + why-free */}
        <div style={{ display: "flex", justifyContent: "center", gap: isMobile ? 10 : 14, flexWrap: "wrap", maxWidth: 920, margin: "32px auto 0" }}>
          {TRACKER_TRUST.map((tp) => (
            <div key={tp.titleKey} style={{
              display: "flex", alignItems: "center", gap: 9,
              background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 100, padding: "9px 16px",
            }}>
              <tp.Icon size={15} color="#a5b4fc" />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,0.82)" }}>{t(tp.titleKey)}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ textAlign: "center", marginTop: 32 }}>
          <a
            href="/free-aba-timesheet-payment-tracker/"
            onClick={(e) => { e.preventDefault(); onLearnMoreTracker(); }}
            style={{ color: "#a5b4fc", fontSize: 13.5, fontWeight: 600, textDecoration: "underline" }}
          >
            {t("therapistLanding.trackerLearnMore", "See everything the Pay & Hours tracker does →")}
          </a>
        </div>
      </div>

      {/* ── VS comparison table ───────────────────────────────────── */}
      <div style={{ background: "#fff", padding: sectionPad }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <h2 style={{ fontSize: 34, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: "0 0 10px" }}>
              {t('therapistLanding.comparisonTitle')}
            </h2>
            <p style={{ fontSize: 15, color: D.textMid, maxWidth: 500, margin: "0 auto" }}>
              {t('therapistLanding.comparisonSubtitle')}
            </p>
          </div>
          <div style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${D.border}` }}>
            {/* Table header */}
            <div style={{
              display: "grid", gridTemplateColumns: isMobile ? "1fr 80px 80px" : "1fr 140px 140px",
              background: `linear-gradient(135deg, ${D.navy}, ${D.navyMid})`,
              padding: "14px 20px",
            }}>
              <div />
              <div style={{ textAlign: "center", fontSize: 13, fontWeight: 700, color: "#a5b4fc" }}>{t('therapistLanding.abaCasesLabel')}</div>
              <div style={{ textAlign: "center", fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>{t('therapistLanding.genericBoardLabel')}</div>
            </div>
            {/* Rows */}
            {VS_ROWS.map((row, i) => (
              <div
                key={row.labelKey || row.label}
                style={{
                  display: "grid", gridTemplateColumns: isMobile ? "1fr 80px 80px" : "1fr 140px 140px",
                  padding: "13px 20px", alignItems: "center",
                  background: i % 2 === 1 ? "#F8FAFC" : "#fff",
                  borderBottom: i < VS_ROWS.length - 1 ? `1px solid ${D.border}` : "none",
                }}
              >
                <div style={{ fontSize: 13, color: D.navy, fontWeight: 500, paddingRight: 12 }}>{row.labelKey ? t(row.labelKey) : row.label}</div>
                <div style={{ textAlign: "center" }}><VsCheck type={row.aba} /></div>
                <div style={{ textAlign: "center" }}><VsCheck type={row.generic} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FAQ ───────────────────────────────────────────────────── */}
      <div style={{ background: "#F8FAFC", padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <h2 style={{ fontSize: 34, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: 0 }}>
              {t('therapistLanding.faqTitle')}
            </h2>
          </div>
          {FAQ_ITEMS.map((item, i) => (
            <FaqItem key={item.qKey || item.q} qKey={item.qKey} aKey={item.aKey} q={item.q} a={item.a} defaultOpen={i === 0} />
          ))}
        </div>
      </div>

      {!embedded && <QuietEnding isMobile={isMobile} />}

      {!embedded && <PublicFooter />}

    </div>
  );
}
